// Sentinel's start-up gate: the shell's AuthGate flow (sign-in -> /whoami +
// /me -> the app) with branded pages for the answers of contract v1.9 §5.0:
// 403 not_member (signed in, but no role in this application's tenant) and
// 403 mfa_required (the tenant asks for a second factor). Other errors and
// the callback stay as in the shell.
import type { ComponentChildren } from "preact";
import { useEffect, useState } from "preact/hooks";
import { useLocation } from "wouter-preact";
import { completeSignIn, currentUser, devMode, ErrorBox, hasAnyRole, HttpError, Loading, loadSession, SignedOut, signIn, signOut } from "@gimasi/giothub-shell";
import { FutureFridgesMark } from "../components/Logo.jsx";
import { useT } from "../i18n";
import { bound } from "../lib/account";

type Phase = "starting" | "signed-out" | "ready" | "not_member" | "mfa_required" | "account_forbidden" | "account_closed" | "error";

function Notice({ title, text, children }: { title: string; text: string; children: ComponentChildren }) {
  return (
    <div style="min-height:100vh;display:flex;align-items:center;justify-content:center;padding:16px;background:var(--nav-bg)">
      <section class="card" style="max-width:460px;width:100%;text-align:center;padding:32px" data-testid="gate-notice">
        <div style="display:flex;justify-content:center"><FutureFridgesMark size={56} /></div>
        <h1 style="margin:16px 0 8px;color:var(--fg);font-size:22px">{title}</h1>
        <p style="color:var(--muted)">{text}</p>
        <div class="row" style="justify-content:center;gap:10px;margin-top:20px">{children}</div>
      </section>
    </div>
  );
}

function Callback() {
  const [, nav] = useLocation();
  const [err, setErr] = useState<unknown>(null);
  useEffect(() => {
    completeSignIn().then((to) => {
      nav(to, { replace: true });
      location.reload();
    }, setErr);
  }, []);
  return err ? <div class="p-6"><ErrorBox error={err} /></div> : <Loading />;
}

export function Gate({ children }: { children: ComponentChildren }) {
  const t = useT();
  const [loc] = useLocation();
  const [phase, setPhase] = useState<Phase>("starting");
  const [error, setError] = useState<unknown>(null);
  useEffect(() => {
    if (loc === "/auth/callback") return;
    (async () => {
      if (!devMode() && !(await currentUser())) return setPhase("signed-out");
      await loadSession();
      setPhase("ready");
    })().catch((e) => {
      if (e instanceof HttpError && e.status === 403 && (e.code === "not_member" || e.code === "mfa_required" || e.code === "account_forbidden" || e.code === "account_closed")) return setPhase(e.code);
      setError(e);
      setPhase("error");
    });
  }, []);
  if (loc === "/auth/callback") return <Callback />;
  if (phase === "starting") return <Loading />;
  if (phase === "signed-out") return <SignedOut />;
  if (phase === "not_member")
    return (
      <Notice title={t("x.notMemberTitle")} text={t("x.notMemberText")}>
        <button class="btn" type="button" onClick={() => signOut()}>{t("x.signOut")}</button>
      </Notice>
    );
  if (phase === "account_forbidden" || phase === "account_closed")
    return (
      <Notice title={t("x.acc.noAccessTitle", { name: bound()?.name || "" })} text={t(phase === "account_closed" ? "x.acc.closedText" : "x.acc.noAccessText", { name: bound()?.name || "" })}>
        <button class="btn" type="button" onClick={() => signOut()}>{t("x.signOut")}</button>
      </Notice>
    );
  if (phase === "mfa_required")
    return (
      <Notice title={t("x.mfaTitle")} text={t("x.mfaText")}>
        <button class="btn" type="button" onClick={() => signIn("/")}>{t("x.signInAgain")}</button>
        <button class="btn" type="button" onClick={() => signOut()}>{t("x.signOut")}</button>
      </Notice>
    );
  if (phase === "error") return <div class="p-6"><ErrorBox error={error} /></div>;
  if (!hasAnyRole.value)
    return (
      <Notice title={t("x.notMemberTitle")} text={t("x.noRoleText")}>
        <button class="btn" type="button" onClick={() => signOut()}>{t("x.signOut")}</button>
      </Notice>
    );
  return <>{children}</>;
}
